import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Button, Modal, Tooltip } from "@mantine/core";
import { bindActionCreators } from "redux";
import { updateAppointmentStatus } from "../../data/actions/appointmentActions";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import {
  IPublishedAppointment,
  IPublishedGameAppointmentDetails,
} from "../../types/appointments/PublishedAppointment";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime } from "../../util/date+helper";
import RejectAppointment from "../appointments/RejectAppointment";
import { PublicRosterAnchor } from "../common/Links";

export interface IUpcomingGameRowProps {
  appointment: IPublishedAppointment<IPublishedGameAppointmentDetails>;
  venue: IVenue;
  didUpdateStatus: (AppointmentStatus) => void;
  actions: {
    updateAppointmentStatus: Function;
  };
}

function UpcomingGameRow(props: IUpcomingGameRowProps) {
  const [isRejectingStatus, beginReject] =
    React.useState<AppointmentStatus | null>(null);
  const [error, updateError] = React.useState("");

  const g = props.appointment;

  // var action: any = <p style={{color: "darkgray"}}>{AppointmentStatus.toString(g.status)}</p>;

  const updateAppointmentStatusWrapped = (status: AppointmentStatus) => () => {
    if (error) {
      updateError("");
    }
    props.actions
      .updateAppointmentStatus(g._id, status)
      .then((apt) => props.didUpdateStatus(status))
      .catch((er) => updateError(er.message));
  };

  const tags = [
    g.details.court,
    g.details.position,
    `$${Number(g.total).toFixed(2)}`,
  ].map((t) => ({ color: "#467bcf", text: t }));
  if (g.details.mode) {
    tags.push({ color: "#467bcf", text: g.details.mode });
  }
  if (g.details.apprentice) {
    tags.push({ text: "Apprentice", color: "#6fa358" });
  }

  let affirmitiveText: string = AppointmentStatus.toString(g.status);
  let affirmitiveAction: React.MouseEventHandler<any> | undefined;
  let rejectStatus: AppointmentStatus | null = AppointmentStatus.rejected;
  let rejectText: string = "Reject";

  if (g.status == AppointmentStatus.published) {
    affirmitiveText = "Confirm";
    affirmitiveAction = updateAppointmentStatusWrapped(
      AppointmentStatus.confirmed,
    );
    rejectStatus = AppointmentStatus.rejected;
  } else if (g.status == AppointmentStatus.confirmed) {
    if (
      g.requiresCompletion &&
      toDateTime(g.start).plus({ minutes: 5 }) < DateTime.utc()
    ) {
      affirmitiveText = "Complete";
      affirmitiveAction = updateAppointmentStatusWrapped(
        AppointmentStatus.completed,
      );
      rejectStatus = AppointmentStatus.notCompleted;
      rejectText = "Report a problem";
    } else {
      // don't offer to complete a game if it's still in progress
      affirmitiveAction = undefined;
    }
  } else if (g.status == AppointmentStatus.completed) {
    rejectStatus = null;
  }

  const rejectButtonId = `reject-button-${g._id}`;
  const confirmComponent = affirmitiveAction ? (
    <Button
      variant="outline"
      style={{ padding: "3px", fontSize: "14px" }}
      color="blue"
      onClick={affirmitiveAction}
    >
      {affirmitiveText}
    </Button>
  ) : (
    <span style={{ color: "darkgray" }}>{affirmitiveText}</span>
  );
  const rejectComponent = rejectStatus && (
    <>
      <Button
        className={g.canReject ? "underlineOnHover" : ""}
        id={rejectButtonId}
        color="text"
        style={{
          padding: "3px",
          fontSize: "12px",
          marginTop: "10px",
          color: "darkgray",
          position: "absolute",
          right: "0",
          textAlign: "right",
        }}
        onClick={() => beginReject(rejectStatus)}
        disabled={!g.canReject}
      >
        <em>{rejectText}</em>
      </Button>
      {!g.canReject && (
        <Tooltip label="This appointment cannot be rejected">
          <span />
        </Tooltip>
      )}
    </>
  );

  const closeReject = () => beginReject(null);

  const time = toDateTime(g.start, props.venue?.tz).toFormat("hh:mm a");
  return (
    <>
      <div style={{ display: "inline-block" }}>
        <div style={{ width: "65%", display: "inline" }}>
          <div style={{ position: "absolute", right: "20px" }}>
            {confirmComponent}
            <br />
            {!!rejectComponent && rejectComponent}
            {g.details.rosterToken && (
              <div
                style={{
                  padding: "3px",
                  fontSize: "12px",
                  marginTop: "40px",
                  width: "150px",
                  position: "absolute",
                  right: "0",
                  textAlign: "right",
                }}
              >
                <PublicRosterAnchor
                  col="darkgray"
                  token={g.details.rosterToken}
                >
                  <em>See published roster</em>
                </PublicRosterAnchor>
              </div>
            )}
          </div>
          <p style={{ marginBottom: "6px" }}>
            <span style={{ fontSize: "16px", marginRight: "15px" }}>
              {time}
            </span>
          </p>
          {/* <p>{tags.map(t => <span style={{padding: "3px", marginRight: "5px", borderRadius: "5px", fontSize: "14px", background: "#f2f7ff", color: "#467bcf"}} key={t}>{t}</span>)}</p> */}
          <p
            style={{ fontSize: "12px", color: "darkgray", marginBottom: "4px" }}
          >
            Officials: <em>{g.details.officials}</em>
          </p>
          <p
            style={{ fontSize: "12px", color: "darkgray", marginBottom: "4px" }}
          >
            {g.details.competition}
          </p>
          {/* <p style={{fontSize: "12px", color: "darkgray", marginBottom: "4px"}}><em>{g.teams}</em></p> */}
          <p
            style={{
              marginBottom: "4px",
              marginTop: "8px",
              alignItems: "right",
            }}
          >
            {tags.map((t, idx) => (
              <span
                style={{
                  padding: "5px",
                  marginRight: "5px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  background: `${t.color}26`,
                  color: t.color,
                }}
                className="noselect"
                key={t.text || idx}
              >
                {t.text?.toLowerCase() || ""}
              </span>
            ))}
          </p>
          {!!error && <Alert color="red">{error}</Alert>}
        </div>
        <div style={{ backgroundColor: "blue", width: "34%" }} />
      </div>
      <hr />
      {isRejectingStatus && (
        <Modal opened={Boolean(isRejectingStatus)} onClose={closeReject} title="Rejecting appointment">
          <RejectAppointment
            appointment={g._id}
            error={error}
            onClose={closeReject}
            onSave={() => props.didUpdateStatus(rejectStatus)}
          />
        </Modal>
      )}
    </>
  );

  // return (<tr>
  //     <td>{} {g.date.format("A")}</td>
  //     <td>{g.court}</td>
  //     <td style={{color: 'darkgray', backgroundColor: posColor}}>{g.position}</td>
  //     <td>{action}</td>
  //     {g.total && g.status == AppointmentStatus.confirmed && <td>${Number(g.total).toFixed(2)}</td>}
  //     {isRejectingStatus && <Modal isOpen={Boolean(isRejectingStatus)} toggle={closeReject}>
  //         <ModalHeader>Rejecting appointment</ModalHeader>
  //         <ModalBody><RejectAppointment appointment={g.appointment} error={error} onClose={closeReject} onSave={() => props.didUpdateStatus(rejectStatus)} /></ModalBody>
  //     </Modal>}
  // </tr>)
}

const mapState2Props = (state, props: IUpcomingGameRowProps) => {
  if (!props.appointment) return;
  return {};
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ updateAppointmentStatus }, dispatch),
  };
}

export default connect(mapState2Props, mapDispatchToProps)(UpcomingGameRow);
